import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { PermissionGrid } from "@/components/admin/PermissionGrid";
import { cn } from "@/lib/utils";

export function RolesPanel({ meta, reload }) {
  const roles = meta?.roles || [];
  const [sel, setSel] = useState(null);
  const [perms, setPerms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const role = roles.find((r) => r.id === sel) || roles[0];
  useEffect(() => { if (role) setPerms(role.permissions || []); }, [role?.id, meta]); // eslint-disable-line react-hooks/exhaustive-deps
  const isSuper = role?.name === "Super Admin";

  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/roles/${role.id}`, { permissions: perms }); toast.success(`${role.name} permissions saved — effective immediately`); reload(); }
    catch (e) { if (e?.message !== "cancelled") toast.error(formatApiError(e?.response?.data?.detail)); } finally { setSaving(false); }
  };
  const create = async () => {
    try { const { data } = await api.post("/admin/roles", { name: newName, permissions: [] }); toast.success("Role created"); setNewOpen(false); setNewName(""); await reload(); setSel(data.id); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const del = async () => {
    try { await api.delete(`/admin/roles/${role.id}`); toast.success("Role deleted"); setSel(null); reload(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[240px_1fr]" data-testid="roles-panel">
      <div className="space-y-1">
        {roles.map((r) => (
          <button key={r.id} onClick={() => setSel(r.id)} data-testid={`role-item-${r.name.replace(/\s+/g, "-").toLowerCase()}`}
            className={cn("w-full rounded-lg border px-3 py-2 text-left transition-colors", role?.id === r.id ? "border-primary bg-primary/10" : "border-border hover:bg-accent")}>
            <p className="text-sm font-semibold">{r.name}</p>
            <p className="text-[11px] text-muted-foreground">{(r.permissions || []).length} permissions{r.system ? " · default" : ""}</p>
          </button>
        ))}
        <Button size="sm" variant="outline" className="mt-2 w-full" onClick={() => setNewOpen(true)} data-testid="create-role-btn"><Plus className="mr-1 h-4 w-4" /> Create Role</Button>
      </div>
      {role && (
        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div><p className="font-head text-lg font-bold">{role.name}</p><p className="text-xs text-muted-foreground">{role.description || "Custom role"}</p></div>
            <div className="flex gap-2">
              {!role.system && <Button size="sm" variant="outline" className="text-rose-500" onClick={del} data-testid="delete-role-btn"><Trash2 className="mr-1 h-4 w-4" /> Delete</Button>}
              <Button size="sm" onClick={save} disabled={saving || isSuper} data-testid="save-role-btn">{saving ? "Saving…" : "Save Role Permissions"}</Button>
            </div>
          </div>
          {isSuper && <p className="mb-3 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-600">Super Admin always has full access and cannot be modified.</p>}
          <PermissionGrid catalog={meta.catalog} mode="role" value={perms} onChange={setPerms} disabled={isSuper} />
        </div>
      )}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="bg-card sm:max-w-sm">
          <DialogHeader><DialogTitle>Create Role</DialogTitle></DialogHeader>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Supervisor" data-testid="new-role-name" />
          <DialogFooter><Button onClick={create} disabled={!newName.trim()} data-testid="new-role-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
